import { ChevronDown, Globe01, Lock01 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import React from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
} from 'react-aria-components';

import { BadgeWithIcon } from '@/lib/base/badges/badges';
import { Popover } from '@/lib/base/overlays/popover';
import type { AccessSummary } from '@/types/crm';

import { AccessPanel } from './AccessPanel';

interface EmailThreadVisibilityBadgeProps {
  access_summary?: AccessSummary | null;
  visibilityActions?: ReactNode;
}

/**
 * Cancels this badge's own `mx-1.5` (below) for containers that need the
 * badge's left edge flush with surrounding content (e.g. the thread
 * sub-header aligning with the record title). COUPLED to the trigger's
 * margin class — the pair is defined here so a margin change updates both.
 */
export const VISIBILITY_BADGE_MARGIN_COMPENSATION = '-ml-1.5';

/**
 * Uses the design-system Tooltip + TooltipTrigger pair — TooltipTrigger wraps
 * an AriaButton so react-aria's TooltipTrigger context can actually anchor the
 * tooltip to the trigger element. A plain HTML `<button>` (the original
 * implementation) was silently broken because react-aria couldn't wire its
 * hover/focus handlers onto it. Matches how `record-header.tsx`,
 * `EnrichIcon`, `EventTimestamp`, etc. render their tooltips.
 *
 * When access_summary is provided the badge becomes interactive: clicking
 * opens an AccessPanel popover with full privacy details. When access_summary
 * is absent or null, renders nothing.
 */

const EmailThreadVisibilityBadgeInner = ({
  access_summary,
  visibilityActions,
}: EmailThreadVisibilityBadgeProps) => {
  if (!access_summary) {
    return null;
  }

  const isOrg = access_summary.privacy_state === 'org';
  return (
    <DialogTrigger>
      {/* w-fit + an explicit pill-shaped focus ring: this trigger is often
          the slideout's first focusable element, so it receives focus on
          open. Without its own focus styling, the browser/react-aria default
          outline stretched to the sub-header row's full width — a giant blue
          box around a tiny "Private" chip. The ring now hugs the pill.
          The disclosure chevron renders INSIDE the capsule (iconTrailing) —
          a chevron floating beside the pill read as a detached fragment. */}
      <AriaButton
        className='mx-1.5 inline-flex w-fit cursor-pointer items-center align-middle rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-offset-1'
        aria-label={
          isOrg
            ? 'Visible to org; click for details'
            : 'Private; click for details'
        }
      >
        <BadgeWithIcon
          iconLeading={isOrg ? Globe01 : Lock01}
          iconTrailing={ChevronDown}
          color={isOrg ? 'brand' : 'gray'}
          type='pill-color'
          size='sm'
        >
          {isOrg ? 'Shared' : 'Private'}
        </BadgeWithIcon>
      </AriaButton>
      {/* Explicit width + `bottom end` placement: the base Popover defaults to
          `w-(--trigger-width)` (correct for select/menu popovers that match
          their trigger), but this badge trigger is a tiny "Private" chip, so
          inheriting it squeezes the panel to one word per line. A fixed width
          plus end-alignment keeps the panel readable and on-screen when the
          badge sits at the far right of the viewport. */}
      <Popover size='md' placement='bottom end' className='w-64!'>
        <Dialog>
          <AccessPanel
            access_summary={access_summary}
            actions={visibilityActions}
          />
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

export const EmailThreadVisibilityBadge = React.memo(
  EmailThreadVisibilityBadgeInner
);
